import { API_METHODS } from '@compass/client/constants/api-methods';
import { Sort } from '@compass/client/types/common';
import { useOrgAndPrograms } from '@hooks/useOrgAndPrograms';
import { ArrowLeft, XCircle } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { AvatarWithStatus } from '@/components/AvatarWithStatus';
import { EmptyState } from '@/components/EmptyState';
import { Breadcrumb, BreadcrumbPortal } from '@/components/Header';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { Skeleton } from '@/components/ui/skeleton';
import { useAPIPermission } from '@/hooks/usePermissions';
import { parseCallDirectionsParam } from '@/pages/qc/utils';
import { useStore } from '@/store';
import { dateToNaiveString, getTodayDateInTimezone, naiveStringToDate } from '@/utils/date';
import { buildOrgRoutePath, removeOrgPrefix } from '@/utils/routes';
import { parseIdListParam, serializeMultiValueParam } from '@/utils/urlParams';

import { AgentRecentEvaluationsTable } from './components/AgentRecentEvaluationsTable';
import { AnalyticsMetricsView } from './components/AnalyticsMetricsView';
import { CallDirectionFilter } from './components/CallDirectionFilter';
import { ViewAllEvaluationsButton } from './components/ViewAllEvaluationsButton';
import { useAgentDetail } from './hooks/useAgentDetail';
import { useAgentRecentEvaluations } from './hooks/useAgentRecentEvaluations';
import { useDownloadChart } from './hooks/useDownloadChart';
import type { AgentMeta } from './types';
import { isResolvedAgentMeta, resolveDisplayAgentMeta } from './utils/agentMeta';
import { getDefaultDateRange } from './utils/dateWindow';
import { buildAnalyticsListPath, resolveAnalyticsOrganizationId } from './utils/resolveOrg';

export const AgentDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { orgId, agentId } = useParams<{ orgId?: string; agentId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { programs, selectedOrganizationId, isLoadingOrgAndPrograms, unauthorizedAccess } =
    useOrgAndPrograms();
  const { getTimeZone } = useStore();
  const timeZone = getTimeZone();
  const cleanOrgId = orgId ? removeOrgPrefix(orgId) : null;
  const organizationId = resolveAnalyticsOrganizationId(cleanOrgId, selectedOrganizationId);
  const analyticsListPath = buildAnalyticsListPath(cleanOrgId);
  const analyticsBackParams = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'agents');
    return params.toString();
  }, [searchParams]);
  const analyticsBackHref = `${analyticsListPath}?${analyticsBackParams}`;

  const start = searchParams.get('start') || '';
  const end = searchParams.get('end') || '';
  const defaultDateRange = useMemo(() => getDefaultDateRange(7, timeZone), [timeZone]);
  const effectiveStart = start || defaultDateRange.start;
  const effectiveEnd = end || defaultDateRange.end;
  const programIds = useMemo(
    () => parseIdListParam(searchParams.get('program_ids')),
    [searchParams],
  );
  const callDirections = useMemo(
    () => parseCallDirectionsParam(searchParams.get('call_directions')),
    [searchParams],
  );

  const updateSearchParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (!v) params.delete(k);
            else params.set(k, v);
          });
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const previousTimeZoneRef = useRef(timeZone);
  const previousDefaultDateRangeRef = useRef(defaultDateRange);

  // Apply last-7-days default when URL has no dates, and update it on timezone
  // changes only if the URL still holds the previous default range.
  useEffect(() => {
    const previousTimeZone = previousTimeZoneRef.current;
    const previousDefaultDateRange = previousDefaultDateRangeRef.current;
    previousTimeZoneRef.current = timeZone;
    previousDefaultDateRangeRef.current = defaultDateRange;

    if (!start || !end) {
      updateSearchParams(defaultDateRange);
      return;
    }

    const isPreviousDefaultDateRange =
      start === previousDefaultDateRange.start && end === previousDefaultDateRange.end;
    if (previousTimeZone !== timeZone && isPreviousDefaultDateRange) {
      updateSearchParams(defaultDateRange);
    }
  }, [defaultDateRange, start, end, timeZone, updateSearchParams]);

  const programOptions = useMemo(
    () =>
      programs
        .filter(p => !!p.id)
        .map(p => ({ label: p.name || p.id || '', value: p.id! }))
        .sort((l, r) => l.label.localeCompare(r.label)),
    [programs],
  );

  const query = useAgentDetail({
    organizationId,
    agentId: agentId ?? null,
    programIds,
    callDirections,
    start: effectiveStart,
    end: effectiveEnd,
    timeZone,
  });
  const data = query.data;
  const loading = query.isLoading;
  const error = query.error;
  const pageLoading = loading || isLoadingOrgAndPrograms;
  const [preservedAgent, setPreservedAgent] = useState<AgentMeta | undefined>();

  useEffect(() => {
    const currentAgent = data?.agent;
    if (currentAgent && isResolvedAgentMeta(currentAgent, agentId)) {
      setPreservedAgent(currentAgent);
    }
  }, [agentId, data?.agent]);

  const displayAgent = useMemo(
    () =>
      resolveDisplayAgentMeta({
        currentAgent: data?.agent,
        preservedAgent,
        routeAgentId: agentId,
      }),
    [agentId, data?.agent, preservedAgent],
  );
  const hasAgentForRoute = !!data?.agent || preservedAgent?.agent_id === agentId;
  const headerLoading = pageLoading && !hasAgentForRoute;

  // Agent detail (and its Recent Calls table) reads per-agent evaluations, so it's
  // gated on evaluation read — same gate as the Agents tab. Analytics-only users
  // that reach this route directly are redirected back to the analytics list.
  const { hasPermission: canViewAgentDetail } = useAPIPermission(
    API_METHODS.EvaluationService.ListEvaluations,
  );

  const recentEvaluationsQuery = useAgentRecentEvaluations({
    organizationId,
    // Gate the fetch too, so a redirecting render never fires a 403.
    agentId: canViewAgentDetail ? (agentId ?? null) : null,
    programIds,
    callDirections,
    start: effectiveStart,
    end: effectiveEnd,
    timeZone,
    limit: 10,
  });

  const download = useDownloadChart();

  const drilldownScope = useMemo(
    () => ({
      dateFrom: effectiveStart,
      dateTo: effectiveEnd,
      programIds,
      agentIds: agentId ? [agentId] : [],
      callDirections,
    }),
    [agentId, callDirections, effectiveEnd, effectiveStart, programIds],
  );

  const viewAllEvalsHref = useMemo(() => {
    if (!organizationId || !agentId) return '#';

    const params = new URLSearchParams();
    params.set('agent_ids', agentId);
    if (programIds.length) {
      const serializedProgramIds = serializeMultiValueParam(programIds);
      if (serializedProgramIds) params.set('program_ids', serializedProgramIds);
    }
    const serializedCallDirections = serializeMultiValueParam(callDirections);
    if (serializedCallDirections) params.set('call_directions', serializedCallDirections);
    if (effectiveStart) params.set('date_from', effectiveStart);
    if (effectiveEnd) params.set('date_to', effectiveEnd);
    params.set('sort_by_call_start_time', String(Sort.SORT_DESC));

    return `${buildOrgRoutePath('/qc', organizationId)}?${params.toString()}`;
  }, [agentId, callDirections, effectiveEnd, effectiveStart, organizationId, programIds]);

  const recentEvaluations = recentEvaluationsQuery.data ?? [];

  // No evaluation read → no agent detail. Send them back to the analytics list
  // (the /analytics route validator keeps org-only analytics access intact).
  if (!canViewAgentDetail) {
    return <Navigate to={analyticsListPath} replace />;
  }

  if (unauthorizedAccess?.isUnauthorized) {
    return (
      <EmptyState
        title="Access Denied"
        description="You do not have access to this organization."
        icon={XCircle}
        action={{ label: 'Go to Home', onClick: () => navigate('/') }}
      />
    );
  }

  return (
    <>
      <BreadcrumbPortal>
        <Breadcrumb>
          <Breadcrumb.Item to={analyticsBackHref}>Analytics</Breadcrumb.Item>
          <Breadcrumb.Item>{displayAgent?.agent_name ?? 'Agent'}</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbPortal>

      <div className="flex h-full flex-col gap-4 px-6 py-6">
        <Link
          to={analyticsBackHref}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground w-fit"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Analytics
        </Link>

        <header className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AvatarWithStatus name={displayAgent?.agent_name} size="lg" />
            <div>
              {headerLoading ? (
                <Skeleton className="h-7 w-56" />
              ) : (
                <h1 className="text-xl font-semibold text-foreground">
                  {displayAgent?.agent_name ?? '—'}
                </h1>
              )}
              <p className="text-sm text-muted-foreground">
                {displayAgent?.program_name ? `${displayAgent.program_name} · ` : ''}
                {displayAgent?.org_name ?? ''}
              </p>
            </div>
          </div>
          {pageLoading ? (
            <Skeleton className="h-8 w-[172px]" />
          ) : (
            <ViewAllEvaluationsButton href={viewAllEvalsHref} />
          )}
        </header>

        <div className="flex flex-wrap items-center gap-3">
          <SearchableMultiSelect
            options={programOptions}
            selected={programIds}
            onChange={next => updateSearchParams({ program_ids: serializeMultiValueParam(next) })}
            placeholder="All Programs"
            className="w-44 px-3"
            contentClassName="w-auto min-w-[220px]"
            searchPlaceholder="Search programs..."
            emptyMessage="No programs found."
          />
          <CallDirectionFilter
            selected={callDirections}
            onChange={next =>
              updateSearchParams({ call_directions: serializeMultiValueParam(next) })
            }
          />
          <DateRangePicker
            // key remounts the picker when an external write updates start/end
            // (e.g. browser back). Internal updates flow through setSearchParams.
            key={`${start}-${end}`}
            initialDateFrom={naiveStringToDate(effectiveStart) ?? getTodayDateInTimezone(timeZone)}
            initialDateTo={naiveStringToDate(effectiveEnd) ?? getTodayDateInTimezone(timeZone)}
            timezone={timeZone}
            showCompare={false}
            onUpdate={({ range }) => {
              if (!range.from) return;
              updateSearchParams({
                start: dateToNaiveString(range.from),
                end: dateToNaiveString(range.to || range.from),
              });
            }}
          />
        </div>

        {error ? (
          <EmptyState
            title="Couldn't load this agent"
            description={
              error instanceof Error
                ? error.message
                : 'The agent detail request failed. Please try again.'
            }
            icon={XCircle}
            action={{ label: 'Retry', onClick: () => query.refetch() }}
          />
        ) : (
          <AnalyticsMetricsView
            data={data}
            loading={pageLoading}
            download={download}
            priorLabel="Organization Average"
            organizationId={organizationId}
            agentId={agentId}
            drilldownScope={drilldownScope}
          />
        )}

        {!error && organizationId ? (
          <AgentRecentEvaluationsTable
            organizationId={organizationId}
            evaluations={recentEvaluations}
            loading={recentEvaluationsQuery.isLoading || recentEvaluationsQuery.isFetching}
            error={recentEvaluationsQuery.error}
            programOptions={programOptions}
            viewAllHref={viewAllEvalsHref}
          />
        ) : null}
      </div>
    </>
  );
};
